import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Palette, Check, ChevronDown, Sparkles, Compass, ChevronRight, LogOut, LifeBuoy } from 'lucide-react';
import { useTheme, THEMES, moodOfTheDay } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { tenurePhaseKey } from '@/lib/tenurePhase';

/**
 * ThemePicker — gold pill that drops down a panel of available themes.
 * Lives in the Dashboard header. Persists choice via ThemeContext (which
 * writes localStorage and respects the admin allow-list).
 *
 * Wrapped in `theme-fixed` so the picker itself stays charcoal+gold
 * regardless of the current theme — keeps the chrome consistent and
 * gives the dropdown a stable surface to render its swatches against.
 */
const ThemePicker = ({ compact = false, floatingDirection = 'down', onLogout = null, desktopIconOnly = false }) => {
  const { theme, mode, setTheme, setMode, available } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const phaseKey = tenurePhaseKey(user);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const visible = THEMES.filter((t) => available.includes(t.key));
  // In auto mode, "current" is whatever today's mood says.
  const effectiveKey = mode === 'auto' ? moodOfTheDay() : theme;
  const current = THEMES.find((t) => t.key === effectiveKey) || THEMES[0];
  const todayMood = THEMES.find((t) => t.key === moodOfTheDay()) || THEMES[0];

  return (
    <div className="theme-fixed relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          desktopIconOnly
            // Owen Feb 2026 round 38 — Niall feedback. Standardised to a
            // 40px gold-bordered circle on every breakpoint so it sits
            // on the same visual baseline as the FloatingHomeBtn swords
            // and the LanguageSwitcher pill. Icon-only on both mobile
            // and desktop — no chevron, no label clutter.
            ? 'inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#D4AF37] bg-[#2A2522]/95 backdrop-blur-sm text-[#D4AF37] hover:bg-[#1C1917] hover:text-[#E8C5A0] hover:shadow-[0_4px_14px_rgba(212,175,55,0.4)] transition-all shadow-[0_3px_10px_rgba(0,0,0,0.5)]'
            : 'inline-flex items-center gap-1.5 h-8 px-2 sm:px-3 rounded-md border border-[#D4AF37] bg-[#2A2522] text-[#D4AF37] hover:bg-[#1C1917] hover:text-[#E8C5A0] transition-colors'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="theme-picker-pill"
        title={`${t('theme_picker.title_prefix')}: ${t(`theme_picker.themes.${current.key}.label`, current.label)}`}
      >
        <Palette className={desktopIconOnly ? 'w-[18px] h-[18px]' : 'w-3.5 h-3.5'} />
        {!compact && !desktopIconOnly && (
          <span
            className="text-xs font-medium hidden sm:inline"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            {mode === 'auto'
              ? `${t('theme_picker.button_auto_prefix')} · ${t(`theme_picker.themes.${current.key}.label`, current.label)}`
              : t(`theme_picker.themes.${current.key}.label`, current.label)}
          </span>
        )}
        {!desktopIconOnly && (
          <ChevronDown
            className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Choose theme"
          // Position: anchored to the trigger.
          //   • Mobile: trigger sits bottom-center, so we anchor the
          //     dropdown's horizontal axis to the trigger centre
          //     (left-1/2 -translate-x-1/2) so it opens centred above
          //     the palette.
          //   • Desktop: trigger sits bottom-right, so anchor to the
          //     right edge (sm:right-0, no translate).
          // Clipping fix: cap height at 80vh + overflow so the menu can
          // never disappear off the top of shorter laptop viewports.
          className={`absolute left-1/2 -translate-x-1/2 sm:left-auto sm:right-0 sm:translate-x-0 w-[18rem] max-w-[calc(100vw-1.5rem)] rounded-lg border border-[#3D3531] bg-[#1F1B19] shadow-2xl z-[60] p-1.5 max-h-[80vh] overflow-y-auto overscroll-contain ${
            floatingDirection === 'up' ? 'bottom-full mb-2' : 'mt-2'
          }`}
          data-testid="theme-picker-dropdown"
        >
          {/* "Explore the Colosseum" — Owen Jun 2026: surface the
              first-five-days tour from inside the palette dropdown so it
              stops being buried behind the secret-door icon. Top of the
              list, visually distinct from the theme swatches, with the
              brand door art tucked in the right corner so people recognise
              this leads to the same "Discovering the Colosseum" surface
              they see in the hamburger menu's secret-door icon. */}
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/discover-colosseum'); }}
            data-testid="palette-explore-colosseum"
            className="w-full text-left flex items-center gap-3 rounded-md px-2.5 py-3 mb-1 bg-gradient-to-r from-[#2A2522] to-[#3D3531] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:from-[#2F2925] hover:to-[#3D3531] transition-all group relative overflow-hidden"
          >
            <Compass className="w-5 h-5 text-[#D4AF37] shrink-0 group-hover:rotate-12 transition-transform relative z-10" />
            <div className="flex-1 min-w-0 relative z-10">
              <p className="text-sm text-[#F4EFE6] leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('theme_picker.explore_title')}
              </p>
              <p className="text-[11px] text-[#9C8E83] mt-0.5 leading-snug" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {t(`tenure_phase.${phaseKey}`)}
              </p>
            </div>
            {/* Brand door art — tucked in the right corner, same arch
                thumbnail as the SecretEntry icon. Faded so the row's
                content still leads visually. */}
            <div
              className="absolute right-0 top-0 bottom-0 w-20 sm:w-24 bg-cover bg-center pointer-events-none opacity-50 group-hover:opacity-70 transition-opacity"
              style={{
                backgroundImage: `url(${process.env.REACT_APP_BACKEND_URL}/api/uploads/static/colosseum_door.jpg?v=2)`,
                backgroundPosition: 'center 40%',
                maskImage: 'linear-gradient(to right, transparent, black 55%)',
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 55%)',
              }}
              aria-hidden
            />
            <ChevronRight className="w-4 h-4 text-[#D4AF37]/60 group-hover:text-[#D4AF37] shrink-0 relative z-10" />
          </button>

          {/* Need help — Owen Feb 2026 round 37. Moved out of the
              floating bottom-right corner (where the LanguageSwitcher
              now lives permanently) and parked here, between
              "Explore the Colosseum" and "Sign out", as a regular menu
              row. Same destination as the old floating lifebuoy: the
              /contact page. */}
          <button
            type="button"
            onClick={() => { setOpen(false); navigate('/contact'); }}
            data-testid="palette-help"
            className="w-full flex items-center gap-3 rounded-md px-2.5 py-2.5 mb-1 hover:bg-[#2A2522] transition-colors text-left"
          >
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#1C1917] border border-[#D4AF37]/40 flex items-center justify-center">
              <LifeBuoy className="w-3.5 h-3.5 text-[#D4AF37]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#F4EFE6] leading-tight" style={{fontFamily:'Cormorant Garamond, serif'}}>
                {t('theme_picker.help_title')}
              </p>
              <p className="text-[11px] text-[#9C8E83] mt-0.5 leading-snug" style={{fontFamily:'Work Sans, sans-serif'}}>
                {t('theme_picker.help_subtitle')}
              </p>
            </div>
          </button>

          {/* Sign out — moved up directly under "Explore the Colosseum" so
              it doesn't get missed at the bottom of a long palette list.
              Owen Feb 2026 round 35 brief: "have log out moved under
              discovering the Colosseum. Can be missed down the bottom." */}
          {onLogout && (
            <button
              type="button"
              onClick={() => { setOpen(false); onLogout(); }}
              data-testid="palette-logout"
              className="w-full flex items-center gap-3 rounded-md px-2.5 py-2.5 mb-1 hover:bg-[#2A2522] transition-colors text-left"
            >
              <span className="shrink-0 w-7 h-7 rounded-full bg-[#1C1917] border border-[#D4AF37]/40 flex items-center justify-center">
                <LogOut className="w-3.5 h-3.5 text-[#D4AF37]" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#F4EFE6] leading-tight" style={{fontFamily:'Cormorant Garamond, serif'}}>
                  {t('theme_picker.signout_title')}
                </p>
                <p className="text-[11px] text-[#9C8E83] mt-0.5 leading-snug" style={{fontFamily:'Work Sans, sans-serif'}}>
                  {t('theme_picker.signout_subtitle')}
                </p>
              </div>
            </button>
          )}

          <p
            className="px-2.5 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.3em] text-[#8A817C]"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            {t('theme_picker.choose_mood')}
          </p>
          {visible.map((th) => {
            const active = th.key === effectiveKey;
            const label = t(`theme_picker.themes.${th.key}.label`, th.label);
            const blurb = t(`theme_picker.themes.${th.key}.blurb`, th.blurb);
            return (
              <button
                key={th.key}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { setTheme(th.key); setOpen(false); }}
                data-testid={`theme-option-${th.key}`}
                className={`w-full text-left flex items-start gap-3 rounded-md px-2.5 py-2.5 transition-colors ${
                  active
                    ? 'bg-[#2A2522] border border-[#D4AF37]/60'
                    : 'hover:bg-[#2A2522] border border-transparent'
                }`}
              >
                {/* Swatch — three stacked discs showing bg / accent / text */}
                <div className="flex shrink-0 mt-1">
                  {th.swatch.map((c, i) => (
                    <span
                      key={c + i}
                      className="block w-4 h-4 rounded-full border border-black/30"
                      style={{ backgroundColor: c, marginLeft: i === 0 ? 0 : -6 }}
                    />
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p
                      className="text-sm text-[#F4EFE6] leading-tight"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {label}
                    </p>
                    {active && <Check className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />}
                    {mode === 'auto' && th.key === todayMood.key && (
                      <span className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/80" style={{fontFamily:'Work Sans, sans-serif'}}>
                        {t('theme_picker.todays_mood')}
                      </span>
                    )}
                  </div>
                  <p
                    className="text-[11px] text-[#9C8E83] mt-0.5 leading-snug"
                    style={{ fontFamily: 'Work Sans, sans-serif' }}
                  >
                    {blurb}
                  </p>
                </div>
              </button>
            );
          })}
          {visible.length === 0 && (
            <p className="px-2.5 py-3 text-xs text-[#9C8E83] italic">
              {t('theme_picker.no_themes')}
            </p>
          )}

          {/* Daily mood (auto) — when ON, the app rotates through a weekly
              mood: Sun slate · Mon-Tue charcoal · Wed klimt · Thu
              charcoal · Fri modern-art · Sat parchment. Picking any
              theme manually flips this back OFF automatically. */}
          <div className="mt-1.5 pt-2 border-t border-[#3D3531]">
            <button
              type="button"
              onClick={() => setMode(mode === 'auto' ? 'manual' : 'auto')}
              data-testid="theme-auto-toggle"
              className="w-full flex items-center gap-3 rounded-md px-2.5 py-2.5 hover:bg-[#2A2522] transition-colors"
              aria-pressed={mode === 'auto'}
            >
              <span className="shrink-0 w-7 h-7 rounded-full bg-[#1C1917] border border-[#D4AF37]/40 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
              </span>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm text-[#F4EFE6] leading-tight" style={{fontFamily:'Cormorant Garamond, serif'}}>
                  {t('theme_picker.daily_mood_title')}
                </p>
                <p className="text-[11px] text-[#9C8E83] mt-0.5 leading-snug" style={{fontFamily:'Work Sans, sans-serif'}}>
                  {mode === 'auto'
                    ? t('theme_picker.daily_mood_on', { mood: t(`theme_picker.themes.${todayMood.key}.label`, todayMood.label) })
                    : t('theme_picker.daily_mood_off')}
                </p>
              </div>
              <span
                className={`shrink-0 inline-flex items-center justify-center w-10 h-6 rounded-full transition-colors ${
                  mode === 'auto' ? 'bg-[#D4AF37]' : 'bg-[#3D3531]'
                }`}
                aria-hidden="true"
              >
                <span
                  className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                    mode === 'auto' ? 'translate-x-2' : '-translate-x-2'
                  }`}
                />
              </span>
            </button>
          </div>

          {/* Sign out has been moved up directly under "Explore the
              Colosseum" — see comment near the top of this dropdown.
              Owen Feb 2026 round 35. */}
        </div>
      )}
    </div>
  );
};

export default ThemePicker;
